import { useCallback } from 'react';
import { BeatAPISavedSearchRepository, pendingAutoFit } from 'ts-core';
import { useApi, useBalloonEventsMetadata, useQuery } from 'react-ui';
import { buildQueryFromSavedSearch } from '../utils/buildQueryFromSavedSearch';

interface ApplyTagOptions {
    /**
     * Add the bare tag layer immediately (snappy UX) before awaiting the saved
     * search lookup. The async merge below replaces it via id-based dedupe —
     * no duplicate chip. Default false (caller awaits the resolved merge).
     */
    optimistic?: boolean;
    /** Skip entirely if a `tag:<slug>` layer is already active. */
    skipIfExists?: boolean;
    /** Forwarded to addQuery — write to URL history. Provider default when omitted. */
    alterHistory?: boolean;
}

/**
 * Single source of truth for "user picked tag X".
 *
 * Adds a `tag:<slug>` query layer AND folds in the tag-bound SYSTEM saved
 * search (forceMapClustering, amount, etc.) when one exists — mirroring the
 * backend's "exactly one tag → merge saved search" rule. Both effects collapse
 * into ONE layer keyed by the tag id, so removing the chip drops the
 * saved-search effects with it.
 *
 * Use this everywhere instead of inlining `addQuery({ tags: [slug] })`. Without
 * the consolidation, callers easily diverge — e.g. a banner click that adds
 * the tag without the saved-search merge silently disables clustering even
 * though `/tags/:slug` and the SearchBar dropdown both engage it.
 */
export function useApplyTagFilter() {
    const { addQuery, has } = useQuery();
    const { apiEndpoints, apiKey } = useApi();
    const { metadata } = useBalloonEventsMetadata();

    return useCallback(async (slug: string, opts: ApplyTagOptions = {}): Promise<void> => {
        const queryId = `tag:${slug}`;

        if (opts.skipIfExists && has(queryId)) return;

        // Past the skip guard a fresh search is guaranteed below — arm the
        // one-shot map auto-fit so MapAutoFitHandler fits to the tag's global
        // bbox once. Covers SearchBar, banner clicks and InitialFilterApplier's
        // tag path (FilterRouteHandler arms its own tags/categories cases).
        pendingAutoFit.arm();

        if (opts.optimistic) {
            addQuery({ id: queryId, tags: [slug] }, opts.alterHistory);
        }

        // Fold in the tag-bound saved search if one exists. Without it (or if
        // the lookup fails / endpoint missing) fall back to the bare tag layer.
        let savedMergeApplied = false;
        if (apiEndpoints?.savedSearches && apiKey) {
            try {
                const repo = new BeatAPISavedSearchRepository(apiEndpoints.savedSearches, apiKey);
                const saved = await repo.getByTag(slug);
                if (saved) {
                    const sQuery = buildQueryFromSavedSearch(saved, metadata);
                    addQuery({ ...sQuery, id: queryId, tags: [slug] }, opts.alterHistory);
                    savedMergeApplied = true;
                }
            } catch (error) {
                console.warn('[useApplyTagFilter] tag-bound saved-search lookup failed:', error);
            }
        }

        // Optimistic mode already added the bare layer; only re-add when there
        // was no saved-search merge AND we weren't optimistic (otherwise the
        // optimistic add is already the final state, no need to re-render).
        if (!savedMergeApplied && !opts.optimistic) {
            addQuery({ id: queryId, tags: [slug] }, opts.alterHistory);
        }
    }, [addQuery, has, apiEndpoints, apiKey, metadata]);
}
